import ContactForm from "../atoms/ContactForm";
import ServicesHeader from "../atoms/ServicesHeader";
import Button from "../components/v2/Button";
import Input from "../components/v2/Input";
import Textarea from "../components/v2/TextArea";
import Typography from "../components/v2/Typography";
import useApp from "../hooks/useApp";
import Faq from "../atoms/Faq";
import ContactSection from "../atoms/Contact";
import BlogSection from "../atoms/BlogPreview";
import { Link } from "react-router-dom";

const Consultation = () => {

    const appCtx = useApp();

    return (
        <>
            <ServicesHeader active="/services/consulting" />
            {/* <!-- 1. HERO --> */}
            <section className="py-20 md:py-28 bg-white consulting-service">
                <div className="container mx-auto px-4 max-w-6xl grid md:grid-cols-2 gap-12 items-start">
                    <div>
                        <h1 className="text-5xl font-bold leading-tight mb-6">Strategic Domain Consulting</h1>
                        <p className="text-xl mb-4">Backed by eight‑figure brokerage wins and former corporate‑registrar expertise.</p>
                        <p className="text-lg mb-6 max-w-xl">From naming a new venture to pruning a 2,000‑name legacy portfolio, NameConnect delivers data‑driven insight, clear next steps, and execution support—all under NDA.</p>
                        <ul className="list-disc list-inside space-y-2 text-lg">
                            <li>Human + AI naming frameworks</li>
                            <li>Portfolio cost‑cutting & monetization</li>
                            <li>Risk mitigation through defensive registrations</li>
                            <li>Market‑tested valuation dashboards</li>
                        </ul>
                    </div>
                    {/* Callback Form */}
                    <div className="border-1 border-blue-500 p-8 shadow-sm bg-gray-50">
                        <h2 className="text-2xl font-semibold mb-4">Send an Inquiry</h2>
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            console.log(e.target);
                            appCtx.submitForm(e.target.name.value, e.target.email.value, e.target.phone.value, e.target.query.value);
                        }} className="space-y-4">
                            <div className="form-control">
                                <div className="form-name">
                                    <Typography variant="para">Name</Typography>
                                </div>
                                <Input isDark={true} name="name" label="" placeholder="John Doe" type="text" value="" onChange={() => { }} />
                            </div>
                            <div className="form-control">
                                <div className="form-name">
                                    <Typography variant="para">Phone number</Typography>
                                </div>
                                <Input isDark={true} name="phone" label="" placeholder="+1 123456789" type="number" value="" onChange={() => { }} />
                            </div>
                            <div className="form-control">
                                <div className="form-name">
                                    <Typography variant="para">Email</Typography>
                                </div>
                                <Input isDark={true} name="email" label="" placeholder="johndoe@gmail.com" type="email" value="" onChange={() => { }} />
                            </div>
                            <Textarea isDark={true} name="query" label="" placeholder="What do you need help with?" value="" onChange={() => { }} style={{height: 170}} />
                            <Button variant="primary" type="submit" fullWidth>
                                <Typography variant="button">Submit Inquiry</Typography>
                            </Button>
                        </form>
                        <p className="text-sm mt-2"><strong>Ready?</strong> Our consultants will respond within 24 hours.</p>
                    </div>
                </div>
            </section>

            {/* 2. WHY CONSULTING MATTERS */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">Why Domain Consulting Matters</h2>
                    <ul className="grid md:grid-cols-2 gap-6 text-lg list-disc list-inside">
                        <li>Portfolio bloat inflates renewal costs & hides saleable assets</li>
                        <li>Unregistered ccTLDs expose brand to phishing & competitor poaching</li>
                        <li>Naming dead‑ends delay product launches and confuse investors</li>
                        <li>Boards need third‑party validation before green‑lighting six‑figure buys</li>
                    </ul>
                </div>
            </section>

            {/* 3. CONSULTING MODULES */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">Consulting Modules</h2>
                    <div className="grid md:grid-cols-2 gap-8 text-lg">
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold mb-2">Portfolio Audit & Rationalization</h3>
                            <p className="mb-3">We run a forensic review of every domain you own, then grade each name as <em>Core / Defensive / Speculative / Liability</em>. You receive:</p>
                            <ul className="list-disc list-inside space-y-1 text-base">
                                <li>Renewal‑cost vs. market‑value matrix (keep / sell / drop)</li>
                                <li>"Hidden‑gem" shortlist primed for outbound sale</li>
                                <li>Registrar‑consolidation & auto‑renew policy map</li>
                                <li>12‑month cash‑flow forecast for reduction or monetization</li>
                            </ul>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold mb-2">Naming & Brand Architecture</h3>
                            <p className="mb-3">A hybrid Human + AI workshop that generates brand‑ready names and aligns them with trademark reality:</p>
                            <ul className="list-disc list-inside space-y-1 text-base">
                                <li>Interactive ideation sprint (linguistics, phonetics, domain match)</li>
                                <li>TM pre‑screen & global class conflict check</li>
                                <li>Shortlists with exact‑match .com availability & alternatives</li>
                                <li>Cross‑product naming tree to future‑proof line extensions</li>
                            </ul>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold mb-2">Acquisition & Sell‑Side Strategy</h3>
                            <p className="mb-3">Deploy our brokering playbook to discreetly buy or divest premium assets:</p>
                            <ul className="list-disc list-inside space-y-1 text-base">
                                <li>Stealth outreach & owner qualification</li>
                                <li>Data‑backed price framing & term sheets</li>
                                <li>Multi‑channel buyer marketing for sales (VC, strategic, funds)</li>
                                <li>Escrow orchestration & legal wrappers</li>
                            </ul>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold mb-2">Defensive Registration & Risk Mitigation</h3>
                            <p className="mb-3">Close the gaps that let phishing and typo‑squats erode your brand:</p>
                            <ul className="list-disc list-inside space-y-1 text-base">
                                <li>ccTLD & typo variant coverage plan</li>
                                <li>Registrar locks, DNSSEC & monitored WHOIS alerts</li>
                                <li>Abuse‑channel monitoring and takedown playbook</li>
                                <li>Quarterly threat‑surface reports for C‑suite</li>
                            </ul>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm md:col-span-2">
                            <h3 className="text-xl font-semibold mb-2">Market Valuation Dashboards</h3>
                            <p className="mb-3">Always‑fresh intelligence to guide buy, hold, or sell calls:</p>
                            <ul className="list-disc list-inside space-y-1 text-base">
                                <li>Live comparable‑sale feed (NameBio, private comps)</li>
                                <li>Traffic & SEO trend overlays from SEMrush & Ahrefs APIs</li>
                                <li>Liquidity scoring (time‑to‑sale probability & buyer pool size)</li>
                                <li>Weekly snapshot emails & on‑demand CSV export</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* 4. WHO BENEFITS */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">Who Benefits from Our Consulting?</h2>
                    <ul className="grid md:grid-cols-2 gap-6 text-lg list-disc list-inside">
                        <li><strong>Startups:</strong> Get help finding the perfect domain to match your brand and budget</li>
                        <li><strong>Businesses with Portfolios:</strong> Optimize your domain investments with a tailored strategy</li>
                        <li><strong>Online Businesses:</strong> Improve your domain naming policy and internet presence</li>
                        <li><strong>Marketing Teams:</strong> Understand the impact of domain choice on SEO and branding</li>
                    </ul>
                </div>
            </section>

            {/* 5. OUR PROCESS */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">How Our Consulting Works</h2>
                    <div className="space-y-8 text-lg">
                        <div>
                            <h3 className="text-2xl font-semibold mb-2">1. Initial Discussion</h3>
                            <p>Share your challenges, goals, and current domain situation.</p>
                        </div>
                        <div>
                            <h3 className="text-2xl font-semibold mb-2">2. Comprehensive Review</h3>
                            <p>We assess your portfolio, needs, and market opportunities.</p>
                        </div>
                        <div>
                            <h3 className="text-2xl font-semibold mb-2">3. Strategic Plan</h3>
                            <p>Receive actionable recommendations for acquisition, management, or sale.</p>
                        </div>
                        <div>
                            <h3 className="text-2xl font-semibold mb-2">4. Ongoing Support</h3>
                            <p>From implementation to monitoring, we guide you every step of the way.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 6. WHAT SETS US APART */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">Why Choose NameConnect for Domain Consulting?</h2>
                    <ul className="space-y-4 text-lg list-disc list-inside">
                        <li><strong>Over 20 years of industry experience</strong> working with startups, corporations, and investors</li>
                        <li><strong>Deep market knowledge</strong> and access to exclusive sales and private portfolios</li>
                        <li><strong>Proven track record</strong> in maximizing domain value for buying and selling</li>
                        <li><strong>Personalized advice</strong> tailored to your business goals, budget, and industry</li>
                        <li><strong>Comprehensive guidance</strong> on legal, marketing, SEO, and technical aspects of domain management</li>
                    </ul>
                </div>
            </section>

            {/* 7. CTA */}
            <section className="py-20 bg-blue-600 text-white text-center">
                <div className="container mx-auto px-4 max-w-4xl">
                    <h2 className="text-4xl font-bold mb-4">Ready to Maximize Your Domain's Potential?</h2>
                    <p className="text-lg mb-8">Contact NameConnect today for a consultation and take the first step toward securing and growing your digital assets with confidence.</p>
                    <a href="/contact" className="inline-block bg-white text-blue-600 font-semibold py-4 px-10 rounded">Schedule a Discovery Call</a>
                </div>
            </section>

            <BlogSection />
        </>
    );
};



export const ServiceShowcaseR = ({ title, description1, description2, buttonText1, buttonText2, buttonLink1, buttonLink2, imageUrl }) => {
    return (
        <section className="service-showcase right-aligned">
            <div className="inner">
                <div className="service-image">
                    <img src={imageUrl} alt={title} />
                </div>
                <div className="service-content">
                    <div className="service-text">
                        <div className="service-heading">
                            <Typography variant="h1">{title}</Typography>
                        </div>
                        <div className="service-para">
                            <Typography variant="para">{description1}</Typography>
                            <Typography variant="para">{description2}</Typography>
                        </div>
                    </div>
                    <div className="service-controls">
                        {buttonText1 && (
                            <Button variant='primary' as={Link} to={buttonLink1}>
                                <Typography variant={'button'}>{buttonText1}</Typography>
                            </Button>
                        )}
                        {buttonText2 && (
                            <Button variant='secondary' as={Link} to={buttonLink2}>
                                <Typography variant={'button'}>{buttonText2}</Typography>
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};


export const ServiceIndividual = ({ serviceData, faqData = [] }) => {
    // const [faqData, setFaqData] = useState([]);

    // useEffect(() => {
    //     // Replace 'YOUR_FAQ_URL_HERE' with the actual URL you want to fetch from
    //     fetch('YOUR_FAQ_URL_HERE')
    //         .then(response => response.json())
    //         .then(data => {
    //             // Assuming the fetched data is an array of { q: string, a: string } objects
    //             setFaqData(data);
    //         })
    //         .catch(error => {
    //             console.error("Error fetching FAQ data:", error);
    //             // Set some manual data in case of an error
    //             setFaqData([
    //                 { q: "What is a domain name?", a: "A domain name is your website address on the internet." },
    //                 { q: "Why is domain name consultation important?", a: "It helps you choose a name that aligns with your brand and goals." },
    //             ]);
    //         });
    // }, []);

    return (
        <>
            <ServicesHeader active={serviceData?.activeLink} />
            <ServiceShowcaseR
                title={serviceData?.showcase?.title}
                description1={serviceData?.showcase?.description1}
                description2={serviceData?.showcase?.description2}
                buttonText1={serviceData?.showcase?.buttonText1}
                buttonText2={serviceData?.showcase?.buttonText2}
                buttonLink1={serviceData?.showcase?.buttonLink1}
                buttonLink2={serviceData?.showcase?.buttonLink2}
                imageUrl={serviceData?.showcase?.imageUrl}
            />

            <section className="service-intro-showcase">
                <div className="inner">
                    <div className="service-content">
                        {serviceData?.sections?.map((section, index) => (
                            <div className="service-text" key={index}>
                                <div className="service-heading">
                                    <Typography variant="h1">{section.title}</Typography>
                                </div>
                                <div className="service-para">
                                    {section.paragraphs && section.paragraphs.map((paragraph, i) => (
                                        <Typography variant="para" key={i}>{paragraph.indexOf(":") > -1 ? (<><b>{paragraph.split(":")[0]}:</b>{paragraph.split(":")[1]}</>) : paragraph}</Typography>
                                    ))}
                                    {section.listItems && (
                                        <ul className="with-points">
                                            {section.listItems.map((item, i) => (
                                                <li key={i}>{item}</li>
                                            ))}
                                        </ul>
                                    )}
                                    {section.faq && (
                                        <Faq data={faqData} useNumber={section.faq.useNumber} noBorder={section.faq.noBorder} leftAligned={section.faq.leftAligned} />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="service-form">
                        <ContactForm />
                    </div>
                </div>
            </section>

            <Faq data={faqData} noBorder={false} leftAligned={false} showHeader={true} />

            <ContactSection />
            <BlogSection />
        </>
    );
};

export default Consultation;